<template>
	<Teleport to="body">
		<div
			v-if="show"
			class="supporting-modal-backdrop"
			@mousedown.self="requestClose"
		>
			<section
				ref="dialogRef"
				class="supporting-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby="supporting-documents-modal-title"
				tabindex="-1"
				@keydown.esc.prevent="requestClose"
			>
				<header class="supporting-modal__header">
					<div class="supporting-modal__heading">
						<h2 id="supporting-documents-modal-title">Supporting documents</h2>
						<p class="supporting-modal__document-label">{{ documentLabel }}</p>
					</div>
					<button
						type="button"
						class="supporting-modal__close"
						aria-label="Close supporting documents"
						:disabled="saving"
						@click="requestClose"
					>
						<BsXLg />
					</button>
				</header>

				<div class="supporting-modal__body">
					<div v-if="loading" class="supporting-modal__loading" role="status">
						<span class="supporting-modal__spinner" aria-hidden="true"></span>
						<span>Loading supporting documents...</span>
					</div>
					<template v-else>
						<AttachmentManager
							v-model="attachments"
							id="supporting-documents-modal-upload"
							:disabled="saving"
							upload-title="Add documents"
							empty-text="No supporting documents yet."
						/>
					</template>
				</div>

				<div v-if="showDiscardPrompt" class="supporting-modal__discard" role="alert">
					<div>
						<strong>Discard unsaved changes?</strong>
						<p>Files added or removed in this window have not been saved.</p>
					</div>
					<div class="supporting-modal__discard-actions">
						<button type="button" class="supporting-modal__button supporting-modal__button--ghost" @click="showDiscardPrompt = false">
							Keep editing
						</button>
						<button type="button" class="supporting-modal__button supporting-modal__button--danger" @click="discardAndClose">
							Discard
						</button>
					</div>
				</div>

				<footer class="supporting-modal__footer">
					<div class="supporting-modal__status" aria-live="polite">
						<span>{{ attachmentCountLabel }}</span>
						<span v-if="isDirty" class="supporting-modal__unsaved">Unsaved changes</span>
					</div>
					<div class="supporting-modal__actions">
						<button
							type="button"
							class="supporting-modal__button supporting-modal__button--secondary"
							:disabled="saving"
							@click="requestClose"
						>
							Close
						</button>
						<button
							type="button"
							class="supporting-modal__button supporting-modal__button--primary"
							:disabled="loading || saving || !isDirty"
							@click="saveChanges"
						>
							{{ saving ? 'Saving...' : 'Save changes' }}
						</button>
					</div>
				</footer>
			</section>
		</div>
	</Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { BsXLg } from '@kalimahapps/vue-icons';
import AttachmentManager from '@/components/catalog/AttachmentManager.vue';
import { ensureAttachmentArray, visibleAttachments } from '@/utils/attachments';
import { getClientDocumentLabel } from '@/utils/clientDocumentRegistry';

const props = defineProps({
	show: {
		type: Boolean,
		default: false,
	},
	document: {
		type: Object,
		default: null,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	saving: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['close', 'save']);
const dialogRef = ref(null);
const attachments = ref([]);
const pristineSnapshot = ref('[]');
const showDiscardPrompt = ref(false);

const serializeAttachments = (value) =>
	JSON.stringify(
		ensureAttachmentArray(value).map((attachment) => ({
			id: attachment.id || null,
			localId: attachment.localId || null,
			name: attachment.name || '',
			size: attachment.size ?? null,
			status: attachment.status || '',
		})),
	);

const resetAttachments = () => {
	attachments.value = ensureAttachmentArray(props.document?.supportingDocuments);
	pristineSnapshot.value = serializeAttachments(attachments.value);
	showDiscardPrompt.value = false;
};

const isDirty = computed(
	() => serializeAttachments(attachments.value) !== pristineSnapshot.value,
);
const visibleCount = computed(() => visibleAttachments(attachments.value).length);
const attachmentCountLabel = computed(
	() => `${visibleCount.value} ${visibleCount.value === 1 ? 'document' : 'documents'}`,
);
const documentLabel = computed(() => {
	const type = getClientDocumentLabel(props.document?.documentType);
	const reference = String(props.document?.reference || '').trim();
	return reference ? `${type} · ${reference}` : type;
});

const requestClose = () => {
	if (props.saving) return;
	if (isDirty.value) {
		showDiscardPrompt.value = true;
		return;
	}
	emit('close');
};

const discardAndClose = () => {
	showDiscardPrompt.value = false;
	emit('close');
};

const saveChanges = () => {
	if (props.loading || props.saving || !isDirty.value) return;
	emit('save', ensureAttachmentArray(attachments.value));
};

watch(
	() => [props.show, props.document],
	async ([show]) => {
		if (!show) return;
		resetAttachments();
		await nextTick();
		dialogRef.value?.focus?.();
	},
	{ immediate: true },
);
</script>

<style scoped>
.supporting-modal-backdrop {
	position: fixed;
	inset: 0;
	z-index: 1400;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(15, 23, 42, 0.52);
	backdrop-filter: blur(2px);
}

.supporting-modal {
	display: flex;
	flex-direction: column;
	width: min(680px, 100%);
	max-height: min(760px, calc(100vh - 48px));
	background: var(--noc-background, #fff);
	border: 1px solid var(--border-color, #dfe3e8);
	border-radius: 14px;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
	overflow: hidden;
	outline: none;
}

.supporting-modal__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px;
	padding: 22px 24px 18px;
	border-bottom: 1px solid var(--border-color-secondary, #ebeef1);
}

.supporting-modal__heading h2 {
	margin: 0;
	font-size: 1.25rem;
	line-height: 1.25;
	color: var(--text-color, #374151);
}

.supporting-modal__document-label {
	margin: 6px 0 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.84rem;
}

.supporting-modal__close {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	padding: 0;
	border: 1px solid transparent;
	border-radius: 8px;
	background: transparent;
	color: var(--text-color-secondary, #6b7280);
	cursor: pointer;
}

.supporting-modal__close:hover:not(:disabled) {
	background: var(--noc-background-secondary, #f5f6f8);
	color: var(--text-color, #374151);
}

.supporting-modal__body {
	min-height: 230px;
	padding: 22px 24px;
	overflow-y: auto;
}

.supporting-modal__loading {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	min-height: 190px;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.9rem;
}

.supporting-modal__spinner {
	width: 18px;
	height: 18px;
	border: 2px solid #d7dbe2;
	border-top-color: var(--noc-blue, #0065ff);
	border-radius: 50%;
	animation: supporting-modal-spin 0.8s linear infinite;
}

.supporting-modal__discard {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 18px;
	margin: 0 24px;
	padding: 12px 14px;
	border: 1px solid #fecaca;
	border-radius: 8px;
	background: #fff7f7;
}

.supporting-modal__discard strong {
	font-size: 0.86rem;
	color: #991b1b;
}

.supporting-modal__discard p {
	margin: 2px 0 0;
	font-size: 0.78rem;
	color: #7f1d1d;
}

.supporting-modal__discard-actions,
.supporting-modal__actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.supporting-modal__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 20px;
	padding: 16px 24px;
	border-top: 1px solid var(--border-color-secondary, #ebeef1);
	background: var(--noc-background, #fff);
}

.supporting-modal__status {
	display: flex;
	align-items: center;
	gap: 10px;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.8rem;
}

.supporting-modal__unsaved {
	color: #b45309;
	font-weight: 600;
}

.supporting-modal__button {
	min-height: 36px;
	padding: 0 14px;
	border: 1px solid transparent;
	border-radius: 8px;
	font: inherit;
	font-size: 0.84rem;
	font-weight: 600;
	cursor: pointer;
}

.supporting-modal__button:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.supporting-modal__button--secondary,
.supporting-modal__button--ghost {
	border-color: var(--border-color, #dfe3e8);
	background: var(--noc-background, #fff);
	color: var(--text-color, #374151);
}

.supporting-modal__button--primary {
	background: var(--noc-blue, #0065ff);
	color: #fff;
}

.supporting-modal__button--danger {
	background: #dc2626;
	color: #fff;
}

@keyframes supporting-modal-spin {
	to { transform: rotate(360deg); }
}

@media (max-width: 620px) {
	.supporting-modal-backdrop {
		align-items: flex-end;
		padding: 0;
	}

	.supporting-modal {
		width: 100%;
		max-height: 92vh;
		border-radius: 14px 14px 0 0;
	}

	.supporting-modal__discard,
	.supporting-modal__footer {
		align-items: stretch;
		flex-direction: column;
	}

	.supporting-modal__discard-actions,
	.supporting-modal__actions {
		justify-content: flex-end;
	}
}
</style>
